// Preferences explorer: view/edit the current user's preferences for a chosen app (Slice 7).
// Two-column layout: the preference table on the left, an "Add / edit" card on the right.
import { useState } from "react";
import type { FormEvent } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";

import type { Client, PreferenceRow } from "../types";
import { MONO } from "../theme";
import { EmptyState, PageHeader } from "../components/ui";
import TuneIcon from "@mui/icons-material/Tune";

export function Preferences({ client }: { client: Client }) {
  const [appName, setAppName] = useState("");
  const [loadedApp, setLoadedApp] = useState(""); // the app whose rows are shown
  const [rows, setRows] = useState<PreferenceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // new/edit form
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [context, setContext] = useState("");

  async function load(e?: FormEvent) {
    e?.preventDefault();
    if (!appName) return;
    setError(null);
    try {
      const res = await client.preferences.scanPreferences({ appName });
      setRows(res.preferences || []);
      setLoadedApp(appName);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      // The form value is JSON (so booleans/arrays work); fall back to a raw string.
      let parsed: unknown;
      try {
        parsed = JSON.parse(value);
      } catch {
        parsed = value;
      }
      await client.preferences.writePreferences({
        appName,
        preferenceName: name,
        value: parsed,
        context: context || undefined,
      });
      setName("");
      setValue("");
      setContext("");
      load();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  // Delete a preference: the API deletes on an empty/null value (see writePreferences).
  async function remove(row: PreferenceRow) {
    setError(null);
    try {
      await client.preferences.writePreferences({
        appName: loadedApp,
        preferenceName: row.preferenceName,
        value: null,
        context: row.context || undefined,
      });
      load();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <Stack spacing={3.5}>
      <PageHeader title="Preferences" subtitle="View and edit your saved preferences for an app." />

      <Stack
        component="form"
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "flex-end" }}
        onSubmit={load}
      >
        <TextField
          label="App name"
          placeholder="e.g. bullseye"
          value={appName}
          onChange={(e) => setAppName(e.target.value)}
          sx={{ width: { xs: "100%", sm: 280 } }}
        />
        <Button type="submit" variant="contained" startIcon={<SearchIcon />}>
          Load preferences
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {rows === null && !error && (
        <EmptyState
          icon={<TuneIcon />}
          title="Load an app's preferences"
          hint="Enter an app name above to view and edit the preferences you've saved for it."
          dense
        />
      )}

      {rows !== null && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 400px" },
            gap: 3,
            alignItems: "start",
          }}
        >
          {/* Left: the preference table */}
          <Box sx={{ flex: 1, minWidth: 320 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Preferences for {loadedApp}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table sx={{ minWidth: 620 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Preference</TableCell>
                    <TableCell sx={{ width: 160 }}>Context</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell sx={{ width: 56 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ color: "text.secondary" }}>
                        No preferences for this app/user.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((p) => (
                      <TableRow key={`${p.preferenceName}:${p.context}`}>
                        <TableCell sx={{ fontWeight: 500 }}>{p.preferenceName}</TableCell>
                        <TableCell sx={{ color: "text.secondary" }}>{p.context}</TableCell>
                        <TableCell>
                          <Box component="code" sx={{ fontFamily: MONO, fontSize: 13 }}>
                            {JSON.stringify(p.value)}
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ px: 0.5 }}>
                          <Tooltip title="Delete preference">
                            <IconButton
                              size="small"
                              aria-label={`Delete ${p.preferenceName}`}
                              onClick={() => remove(p)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Right: add / edit a preference */}
          <Card sx={{ width: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6">Add / edit a preference</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, mb: 2.5 }}>
                Values are JSON; an empty value deletes the preference.
              </Typography>
              <Stack component="form" spacing={2} onSubmit={save}>
                <TextField
                  label="Preference name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Context (optional)"
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Value (JSON)"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  fullWidth
                  slotProps={{ input: { sx: { fontFamily: MONO, fontSize: 13 } } }}
                />
                <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={!name} fullWidth>
                  Save preference
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}
    </Stack>
  );
}
